import { countryLabels } from '@/constant/countries';
import clsx from 'clsx';
import { useEffect, useRef } from 'react';
import FlagsSelect from 'react-flags-select';
import { IntlShape } from 'react-intl';
import countries from 'world-countries';

interface CountryCodeSelectProps {
  id?: string;
  disabled?: boolean;
  className?: string;
  selected: string; // country short code (e.g., "US", "IN")
  setFieldValue: (code: string, shortCode: string) => void;
  hasError?: boolean;
  intl?: IntlShape;
  showDialCode?: boolean;
  useFullCountryName?: boolean;
  ariaLabel?: string;
  ariaLabelledBy?: string;
  ariaDescribedBy?: string;
}

const fullCountryLabels: typeof countryLabels = Object.keys(
  countryLabels
).reduce(
  (acc, code) => {
    const base =
      countryLabels[code as keyof typeof countryLabels] ||
      ({
        primary: code,
        secondary: '',
        demonym: '',
      } as (typeof countryLabels)[keyof typeof countryLabels]);
    const meta = countries.find((c) => c.cca2 === code);
    const fullName = meta?.name?.common || base.primary || code;
    acc[code as keyof typeof countryLabels] = {
      ...base,
      primary: fullName,
    };
    return acc;
  },
  {} as typeof countryLabels
);

const withIsoSearchLabels = (
  labels: typeof countryLabels
): typeof countryLabels =>
  Object.keys(labels).reduce((acc, code) => {
    const base = labels[code as keyof typeof countryLabels];
    acc[code as keyof typeof countryLabels] = {
      ...base,
      primary: `${base.primary} ${code}`,
    };
    return acc;
  }, {} as typeof countryLabels);

const searchableCountryLabels = withIsoSearchLabels(countryLabels);
const searchableFullCountryLabels = withIsoSearchLabels(fullCountryLabels);

const CountryCodeSelect = ({
  id,
  disabled,
  className,
  selected,
  setFieldValue,
  hasError,
  intl,
  ariaLabel,
  ariaLabelledBy,
  ariaDescribedBy,
  showDialCode = true,
  useFullCountryName = false,
}: CountryCodeSelectProps) => {
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const observer = new MutationObserver(() => {
      const list = rootRef.current?.querySelector(
        '.ReactFlagsSelect-module_selectOptions__3LNBJ'
      );

      if (!list) {
        const root = rootRef.current;
        if (!root) return;
        const staleFilterBoxes = root.querySelectorAll(
          '.ReactFlagsSelect-module_filterBox__3m8EU'
        );
        staleFilterBoxes.forEach((node) => node.remove());
        return;
      }
      const options = list?.querySelectorAll(
        '.ReactFlagsSelect-module_selectOption__3pcgW'
      );
      const noDataId = `custom-no-data-message-${id || 'country-code-select'}`;
      const existingNoData = list?.querySelector(
        `#${noDataId}`
      ) as HTMLElement | null;

      if (list) {
        const label = ariaLabel || 'Country code selector';

        if (!list.getAttribute('aria-label')) {
          list.setAttribute('aria-label', label);
        }

        if (ariaLabelledBy) {
          list.setAttribute('aria-labelledby', ariaLabelledBy);
        } else {
          const button = rootRef.current?.querySelector('button');
          const buttonId = button?.getAttribute('id');
          if (buttonId && !list.getAttribute('aria-labelledby')) {
            list.setAttribute('aria-labelledby', buttonId);
          }
        }

        if (!list.getAttribute('title')) {
          list.setAttribute('title', label);
        }

        const filterBox = list.querySelector(
          '.ReactFlagsSelect-module_filterBox__3m8EU'
        );

        if (filterBox) {
          const parent = list.parentElement;
          if (parent && filterBox.parentElement === list) {
            const filterBoxes = parent.querySelectorAll(
              '.ReactFlagsSelect-module_filterBox__3m8EU'
            );
            filterBoxes.forEach((node) => {
              if (node !== filterBox && node.parentElement !== list) {
                node.remove();
              }
            });
            parent.insertBefore(filterBox, list);
          }
        }
      }

      if (list && options?.length === 0 && !existingNoData) {
        const noData = document.createElement('div');
        noData.id = noDataId;
        noData.textContent = intl
          ? intl.formatMessage({ id: 'COMMON.NO_DATA_FOUND' })
          : 'No Data Found';
        noData.className =
          'text-center py-2 px-2 text-sm text-gray-500 font-medium contrast:!text-white';
        list.appendChild(noData);
      } else if ((options?.length ?? 0) > 0 && existingNoData) {
        existingNoData.remove();
      }
    });

    const container = rootRef.current?.querySelector(
      '.ReactFlagsSelect-module_flagsSelect__2pfa2'
    );

    if (container) {
      observer.observe(container, {
        childList: true,
        subtree: true,
      });
    }

    return () => observer.disconnect();
  }, [ariaLabel, ariaLabelledBy, id, intl]);

  // Fix WCAG issues: Add aria-label to listbox and hide search input from accessibility tree
  useEffect(() => {
    const observer = new MutationObserver(() => {
      const container = id
        ? document.querySelector(
            `[data-testid="country-select-container-${id}"]`
          )
        : document.querySelector('.ReactFlagsSelect-module_flagsSelect__2pfa2');

      if (!container) return;

      const list = container.querySelector(
        '.ReactFlagsSelect-module_selectOptions__3LNBJ'
      ) as HTMLElement | null;

      if (list) {
        // Fix 1: Add accessible name to the listbox (ul)
        if (!list.getAttribute('aria-label')) {
          const label =
            ariaLabel ||
            intl?.formatMessage({ id: 'COMMON.COUNTRY_CODE_OPTIONS' }) ||
            'Country options';
          list.setAttribute('aria-label', label);
        }

        // Fix 2: Hide search box from accessibility tree
        // This fixes "Certain ARIA roles must contain particular children" error
        const filterBox = list.querySelector(
          '.ReactFlagsSelect-module_filterBox__3m8EU'
        ) as HTMLElement | null;

        if (filterBox && !filterBox.getAttribute('aria-hidden')) {
          filterBox.setAttribute('aria-hidden', 'true');

          // When aria-hidden="true", all focusable elements inside must have tabindex="-1"
          const input = filterBox.querySelector('input');
          if (input && input.getAttribute('tabindex') !== '-1') {
            input.setAttribute('tabindex', '-1');
          }
        }

        // Fix 3: Ensure listbox children have role="option"
        const options = list.querySelectorAll(
          '.ReactFlagsSelect-module_selectOption__3pcgW'
        );

        options.forEach((option) => {
          const htmlOption = option as HTMLElement;
          if (!htmlOption.getAttribute('role')) {
            htmlOption.setAttribute('role', 'option');
          }
          if (!htmlOption.hasAttribute('aria-selected')) {
            htmlOption.setAttribute('aria-selected', 'false');
          }
        });
      }

      // Fix 4: Add aria-label to button for "Buttons must have discernible text"
      const button = container.querySelector('#rfs-btn') as HTMLElement | null;
      if (button && !button.getAttribute('aria-label')) {
        const buttonLabel = intl
          ? intl.formatMessage({ id: 'COMMON.SELECT_COUNTRY' })
          : 'Select country';
        button.setAttribute('aria-label', buttonLabel);
      }

      const searchInput = container.querySelector(
        '.ReactFlagsSelect-module_filterBox__3m8EU input'
      ) as HTMLInputElement | null;

      if (searchInput) {
        const placeholder = intl
          ? intl.formatMessage({ id: 'COMMON.SEARCH' })
          : 'Search';
        if (searchInput.getAttribute('placeholder') !== placeholder) {
          searchInput.setAttribute('placeholder', placeholder);
        }

        // Ensure search input has an accessible name
        if (!searchInput.getAttribute('aria-label')) {
          searchInput.setAttribute('aria-label', placeholder);
        }

        const targetClasses = clsx(
          'w-full h-10 border-gray-300 text-gray-700 text-sm rounded-md focus:ring-primary focus:border-primary contrast:!bg-light-active contrast:!text-white contrast:!border-gray-600 contrast:placeholder:!text-white/25',
          hasError &&
            'border-red-500 focus:ring-red-500 contrast:!border-[#FF8B33]'
        );

        if (searchInput.className !== targetClasses) {
          searchInput.className = targetClasses;
        }
      }
    });

    // Observe document body to catch the component when it renders
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, [intl, id, ariaLabel, hasError]);

  // Remove auto-set default. Let parent handle empty/default value.

  useEffect(() => {
    const observer = new MutationObserver(() => {
      const searchInput = rootRef.current?.querySelector(
        '.ReactFlagsSelect-module_filterBox__3m8EU input'
      ) as HTMLInputElement | null;

      if (searchInput) {
        const placeholder = intl
          ? intl.formatMessage({ id: 'COMMON.SEARCH' })
          : 'Search';
        searchInput.setAttribute('placeholder', placeholder);
        searchInput.className = clsx(
          searchInput.className,
          'w-full h-10 border-gray-300 text-gray-700 text-sm rounded-md focus:ring-primary focus:border-primary contrast:!bg-light-active contrast:!text-white contrast:!border-gray-600 contrast:placeholder:!text-white/25',
          hasError &&
            'border-red-500 focus:ring-red-500 contrast:!border-[#FF8B33]'
        );
      }
    });

    const container = rootRef.current?.querySelector(
      '.ReactFlagsSelect-module_flagsSelect__2pfa2'
    );

    if (container) {
      observer.observe(container, { childList: true, subtree: true });
    }

    return () => observer.disconnect();
  }, [hasError, intl]);

  const handleSelect = (countryShortCode: string) => {
    const dialCode =
      countryLabels[countryShortCode as keyof typeof countryLabels]
        ?.secondary || '+966';
    setFieldValue(dialCode, countryShortCode);
  };

  useEffect(() => {
    const button = rootRef.current?.querySelector('button');
    if (!button) return;

    if (id) {
      button.id = id;
    }

    const label = ariaLabel || 'Country code selector';
    if (!button.getAttribute('aria-label')) {
      button.setAttribute('aria-label', label);
    }
    if (!button.getAttribute('title')) {
      button.setAttribute('title', label);
    }

    if (ariaLabelledBy) {
      button.setAttribute('aria-labelledby', ariaLabelledBy);
    }

    if (ariaDescribedBy) {
      button.setAttribute('aria-describedby', ariaDescribedBy);
    }
  }, [ariaDescribedBy, ariaLabel, ariaLabelledBy, id, selected]);

  return (
    <div
      ref={rootRef}
      className={clsx('relative w-full', className)}
      data-testid={id ? `country-select-container-${id}` : undefined}
    >
      <FlagsSelect
        aria-label={ariaLabel || 'Country code selector'}
        key={selected} // Force re-render on selected change
        selected={selected}
        onSelect={handleSelect}
        searchable={true}
        disabled={disabled}
        className={clsx(
          'text-sm',
          hasError ? 'border-red-500' : 'border-gray-300'
        )}
        selectButtonClassName={clsx(
          'w-full h-10 border rounded-md text-sm text-gray-700 bg-gray-100 dark:text-gray-800 dark:bg-coal-600 dark:border-coal-100 focus:ring-primary focus:border-primary contrast:!bg-light-active contrast:!text-white contrast:!border-gray-600',
          disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-gray-100',
          hasError &&
            'border-red-500 focus:ring-red-500 contrast:!border-[#FF8B33]'
        )}
        showSelectedLabel={false}
        showSecondarySelectedLabel={showDialCode && !useFullCountryName}
        showOptionLabel={true}
        showSecondaryOptionLabel={showDialCode && !useFullCountryName}
        customLabels={
          useFullCountryName
            ? searchableFullCountryLabels
            : searchableCountryLabels
        }
        countries={Object.keys(countryLabels)}
        placeholder={
          intl ? intl.formatMessage({ id: 'COMMON.SEARCH' }) : 'Search'
        }
      />
      {useFullCountryName && selected && (
        <span
          className={clsx(
            'pointer-events-none absolute top-0 h-10 left-10 flex items-center text-sm text-gray-700 truncate pr-3 z-10 contrast:!text-white',
            disabled && 'text-gray-400 contrast:!text-gray-600'
          )}
        >
          {fullCountryLabels[selected as keyof typeof fullCountryLabels]
            ?.primary || selected}
        </span>
      )}
    </div>
  );
};

export default CountryCodeSelect;
